import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import MyTrainingPanel from "containers/my-training-container/my-training-panel";
import UserProfileMedical from "containers/user-profile-medical/user-profile-medical";
import UserAssignedRoles, {
  useUnsignedRoleDocumentCount,
} from "containers/user-profile/user-assigned-roles";
import { btnLinkCls } from "components/tw/classes";
import { formatDay } from "features/personnel/personnel-helpers";
import { Translate } from "features/cabinet-tasks/task-row";
import { useCabinetSummary } from "features/cabinet-tasks/use-cabinet-summary";
import { CabinetSummary } from "services/cabinet.service";

interface SafetyFacts {
  /** null when the roles could not be loaded, so nobody knows. */
  unsignedDocuments: number | null;
  medical: CabinetSummary["personal"]["medical"];
  trainingsWaiting: number;
}

/**
 * The line over the three blocks. How it is read: the first thing that applies wins, and
 * the first three are things the person has to do (shown in amber); when none of them is
 * left, the line says everything is in order and, if the summary knows it, how long the
 * medical certificate is valid. When the unsigned documents are unknown, nothing can say
 * that everything is in order, so the line is left out in that case.
 */
function safetyStatus({ unsignedDocuments, medical, trainingsWaiting }: SafetyFacts, t: Translate) {
  if (unsignedDocuments !== null && unsignedDocuments > 0) {
    return { text: t("cabinet_safety_documents_to_sign", { count: unsignedDocuments }), needsAction: true };
  }
  if (medical.state === "upload_needed") {
    return { text: t("cabinet_safety_upload_medical"), needsAction: true };
  }
  if (trainingsWaiting > 0) {
    return { text: t("cabinet_safety_trainings_waiting", { count: trainingsWaiting }), needsAction: true };
  }
  if (unsignedDocuments === null) return null;
  const allInOrder = t("cabinet_safety_all_in_order");
  if (!medical.validUntil) return { text: allInOrder, needsAction: false };
  const validUntil = t("cabinet_safety_medical_valid_until", { date: formatDay(medical.validUntil) });
  return { text: `${allInOrder} · ${validUntil}`, needsAction: false };
}

function SafetyBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold text-gray-900">{title}</h2>
      {children}
    </section>
  );
}

/**
 * «Darbuotojo vidiniai dokumentai» in the cabinet: a status line over three blocks, the safety instructions
 * with their documents, the trainings and the medical check. The line is drawn once the summary
 * and the roles have both answered, so it never says "all in order" before it knows. If the roles
 * cannot be loaded, the line goes by the summary alone and a quiet note offers another try.
 */
export default function SafetySection({ companyId, userId }: { companyId: string; userId: string }) {
  const { t } = useTranslation();
  const { summary } = useCabinetSummary(companyId);
  const roles = useUnsignedRoleDocumentCount(companyId, userId, summary);
  // Moving this number loads the list of roles again (it only reads its cache when it opens).
  const [rolesListAttempt, setRolesListAttempt] = useState(0);

  const retryRoles = () => {
    roles.reload();
    setRolesListAttempt((attempt) => attempt + 1);
  };

  const rolesHaveAnswered = roles.count !== null || roles.hasFailed;
  const status =
    summary && rolesHaveAnswered
      ? safetyStatus(
          {
            unsignedDocuments: roles.count,
            medical: summary.personal.medical,
            trainingsWaiting: summary.personal.training.count,
          },
          t
        )
      : null;

  return (
    <div className="space-y-6">
      {(status || roles.hasFailed) && (
        <div className="space-y-1">
          {status && (
            <p className={`text-base font-semibold ${status.needsAction ? "text-amber-700" : "text-gray-900"}`}>
              {status.text}
            </p>
          )}
          {roles.hasFailed && (
            <p className="flex flex-wrap items-center gap-x-2 text-sm text-gray-500">
              {t("cabinet_safety_documents_check_failed")}
              <button type="button" className={btnLinkCls} onClick={retryRoles}>
                {t("try_again")}
              </button>
            </p>
          )}
        </div>
      )}
      <SafetyBlock title={t("cabinet_safety_block_instructions")}>
        <UserAssignedRoles key={rolesListAttempt} companyId={companyId} userId={userId} hideHeading />
      </SafetyBlock>
      <SafetyBlock title={t("cabinet_safety_block_training")}>
        <MyTrainingPanel hideHeading />
      </SafetyBlock>
      <SafetyBlock title={t("cabinet_safety_block_medical")}>
        <UserProfileMedical companyId={companyId} userId={userId} />
      </SafetyBlock>
    </div>
  );
}
